import { FC } from 'react';

import { Formik, type FormikHelpers } from 'formik';
import { useDealsContext } from 'modules/account/sales/containers/Deals/Deals';
import { useGetDeal, useUpdateDeal } from 'modules/account/sales/hooks';
import { Pipeline, SimpleLead, User } from 'utils/types';
import { DEAL_FORM, DEAL_SCHEMA } from 'modules/account/sales/forms/schema';
import EditDealForm from 'modules/account/sales/forms/EditDealForm';

import useModal from '../Modal/useModal';

type ComposeDealBodyProps = {
  mode: 'popover' | 'modal';
};

const ComposerDealBody: FC<ComposeDealBodyProps> = ({ mode = 'popover' }) => {
  const { hideModal } = useModal();
  const { selectedDealId } = useDealsContext();
  const { data: deal } = useGetDeal(selectedDealId);
  const { mutate: updateDeal, isLoading } = useUpdateDeal();

  if (!deal) {
    return <div />;
  }

  const FORM_INIT = {
    name: deal.name ?? '',
    amount: deal.amount ?? 0,
    type: deal.type ?? '',
    pipelineId: (deal.pipelineId as Pipeline)._id ?? '',
    stageId: deal.stageId ?? '',
    closedAt: deal.closedAt ?? null,
    leadId: (deal.leadId as SimpleLead[]).map((lead) => lead._id),
    companyId: deal.companyId?._id ?? null,
    accountUserId:
      deal.accountUserId.map((accountUser) => (accountUser as User)._id) ?? [],
    tagId: deal.tagId,
    data: deal.data,
  } as DEAL_FORM;

  const onSubmit = (
    value: DEAL_FORM,
    { resetForm, setSubmitting }: FormikHelpers<DEAL_FORM>,
  ) => {
    const castValues = DEAL_SCHEMA.cast(value);

    const tagId = castValues.tagId?.map((tag) =>
      typeof tag === 'string' ? tag : tag?._id,
    );

    updateDeal(
      {
        id: selectedDealId as string,
        ...castValues,
        tagId,
        isFullUpdate: true,
        amount: castValues.amount ?? 0,
      },
      {
        onSuccess: () => {
          hideModal();
          resetForm();
        },
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  return (
    <Formik<DEAL_FORM>
      initialValues={FORM_INIT}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={DEAL_SCHEMA}
    >
      {() => <EditDealForm deal={deal} mode={mode} isLoading={isLoading} />}
    </Formik>
  );
};

export default ComposerDealBody;
